import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, type ProjectSummary } from "./api";
import { Header } from "./Header";
import { navigate } from "./router";
import { actorName, setActorName } from "./useDocSync";

const STAR = "cl.starredProjects", RECENT = "cl.recentProjects";
const readList = (k: string): string[] => { try { return JSON.parse(localStorage.getItem(k) ?? "[]"); } catch { return []; } };
export function markRecent(id: string) { localStorage.setItem(RECENT, JSON.stringify([id, ...readList(RECENT).filter((x) => x !== id)].slice(0, 12))); }

export function Projects({ onSignOut }: { onSignOut: () => void }) {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [starred, setStarred] = useState(() => readList(STAR));
  const [renaming, setRenaming] = useState<string | null>(null);
  const [me, setMe] = useState(actorName());
  const recent = readList(RECENT);

  const load = () => api.projects(showArchived ? "all" : undefined).then((r) => setProjects(r.projects)).catch((e) => setError(e.message));
  useEffect(() => { void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { const { project } = await api.createProject(name.trim()); markRecent(project.id); navigate({ name: "project", id: project.id }); }
    catch (err) { setError((err as Error).message); }
  };
  const toggleStar = (id: string) => { const next = starred.includes(id) ? starred.filter((x) => x !== id) : [id, ...starred]; localStorage.setItem(STAR, JSON.stringify(next)); setStarred(next); };
  const act = async (fn: () => Promise<unknown>) => { try { await fn(); await load(); } catch (e) { setError((e as Error).message); } };

  const { pinned, recentList, rest } = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (projects ?? []).filter((p) => !term || p.name.toLowerCase().includes(term));
    const pinned = list.filter((p) => starred.includes(p.id) && !p.archivedAt);
    const recentList = term ? [] : recent.map((id) => list.find((p) => p.id === id)).filter((p): p is ProjectSummary => !!p && !starred.includes(p.id) && !p.archivedAt).slice(0, 4);
    const shown = new Set([...pinned, ...recentList].map((p) => p.id));
    return { pinned, recentList, rest: list.filter((p) => !shown.has(p.id)).sort((a, b) => Number(!!a.archivedAt) - Number(!!b.archivedAt) || a.name.localeCompare(b.name)) };
  }, [projects, q, starred, recent]);

  const card = (p: ProjectSummary) => (
    <div key={p.id} className={`card project-card ${p.archivedAt ? "archived" : ""}`}>
      <button className={`star ${starred.includes(p.id) ? "on" : ""}`} aria-label={starred.includes(p.id) ? "Unpin" : "Pin to top"} title="Pin to top (this device)" onClick={() => toggleStar(p.id)}>★</button>
      {renaming === p.id ? (
        <form onSubmit={(e) => { e.preventDefault(); const v = (new FormData(e.currentTarget).get("n") as string).trim(); if (v) void act(() => api.renameProject(p.id, v)); setRenaming(null); }}>
          <input name="n" defaultValue={p.name} autoFocus onBlur={() => setRenaming(null)} />
        </form>
      ) : (
        <a className="title" href={`#/p/${p.id}`} onClick={() => markRecent(p.id)}>{p.name}</a>
      )}
      <div className="muted small">{p.documentCount} plan set{p.documentCount === 1 ? "" : "s"} · {p.pageCount} sheet{p.pageCount === 1 ? "" : "s"}{p.archivedAt ? " · archived" : ""}</div>
      <div className="muted small">Updated {new Date(p.updatedAt).toLocaleDateString()}</div>
      <div className="card-actions">
        <button className="link" onClick={() => setRenaming(p.id)}>Rename</button>
        {p.archivedAt
          ? <button className="link" onClick={() => act(() => api.archiveProject(p.id, false))}>Restore</button>
          : <button className="link" onClick={() => { if (confirm(`Archive “${p.name}”? It's hidden from this list; nothing is deleted.`)) void act(() => api.archiveProject(p.id, true)); }}>Archive</button>}
      </div>
    </div>
  );

  return (
    <div className="page">
      <Header onSignOut={onSignOut}><span>Projects</span></Header>
      <main className="content home">
        <div className="home-top">
          <input className="search" type="search" placeholder="Search projects…" value={q} onChange={(e) => setQ(e.target.value)} />
          <form className="row new-project" onSubmit={create}>
            <input placeholder="New project, e.g. SR 26 Resurfacing" value={name} onChange={(e) => setName(e.target.value)} />
            <button className="primary" disabled={!name.trim()}>Create</button>
          </form>
        </div>
        <div className="row wrap me-row">
          <span className="muted small">You appear to others as</span>
          <input className="me" value={me} onChange={(e) => setMe(e.target.value)} onBlur={() => setActorName(me || "Web user")} aria-label="Your name" />
          <label className="muted small"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived</label>
        </div>
        {error && <div className="error">{error}</div>}
        {!projects && !error && <div className="muted">Loading projects…</div>}
        {projects?.length === 0 && <div className="empty">No projects yet. Create one, then upload plan sets.</div>}
        {pinned.length > 0 && <><h3 className="home-h">Pinned</h3><div className="grid">{pinned.map(card)}</div></>}
        {recentList.length > 0 && <><h3 className="home-h">Recent</h3><div className="grid">{recentList.map(card)}</div></>}
        {rest.length > 0 && <><h3 className="home-h">{pinned.length || recentList.length ? "All projects" : "Projects"}</h3><div className="grid">{rest.map(card)}</div></>}
        {projects && q && !pinned.length && !rest.length && <div className="empty">No project matches “{q}”.</div>}
      </main>
    </div>
  );
}
